import React from 'react'
import { useSelector } from 'react-redux'
import { isAnyOf } from '@reduxjs/toolkit'
import {
  isFobMatchedSelection,
  isFobSelection,
  isToteSelection,
} from '@mobi/betslip/helpers/typeGuards'
import type { BetSlipItem } from '@mobi/betslip/types'
import { selectBetSlipItems } from '@mobi/betslip/Store/Bets/selectors'
import {
  addItemsToBetSlip,
  clearAllBetSlipItems,
  removeItemFromBetSlipById,
  clearAllReceiptsFromItems,
  removeItemsWithReceipt,
  removeItemsWithFatalError,
} from '@mobi/betslip/Store/Bets'
import dayjs from '@mobi/utils/date/extendedDayjs'
import { store } from '@core/Store'
import { getEnhancedBetslipSetting } from '@core/Areas/Settings/Store/selectors'
import type { SmartBetslipState } from '@core/Areas/Blackbook/types'
import { startListening } from '@core/Store/listenerMiddleware'

/**
 * @param includeTote true if logic should be applied to totes instead of fobs (the 2 are calculated separately)
 * @returns
 */
export function useSmartBetSlip(opts: { includeTote?: boolean } = {}) {
  const includeTote = opts.includeTote || false
  const isSmartBetslipActive = useSelector(getEnhancedBetslipSetting)

  const [state, setState] = React.useState<SmartBetslipState>({
    shouldAutoAddToBetslip: false,
    isSmartBetslipFeatureActive: isSmartBetslipActive,
    betslipFobPropSeqs: [],
    betslipToteIds: [],
  })

  React.useEffect(() => {
    if (!isSmartBetslipActive) {
      return
    }

    const handler = () => {
      const items = selectBetSlipItems(store.getState())
      setState(deriveReactStateFromBetSlip(includeTote, isSmartBetslipActive, items))
    }
    handler()
    const dispose = startListening({
      matcher: isAnyOf(
        addItemsToBetSlip,
        clearAllBetSlipItems,
        removeItemFromBetSlipById,
        clearAllReceiptsFromItems,
        removeItemsWithReceipt,
        removeItemsWithFatalError
      ),
      effect: handler,
    })
    return () => dispose()
  }, [isSmartBetslipActive, includeTote])

  React.useEffect(() => {
    if (!isSmartBetslipActive) {
      setState({
        shouldAutoAddToBetslip: false,
        isSmartBetslipFeatureActive: isSmartBetslipActive,
        betslipFobPropSeqs: [],
        betslipToteIds: [],
      })
    }
  }, [isSmartBetslipActive])

  return state
}

// =============
// Local Helpers
// =============

function deriveReactStateFromBetSlip(
  includeTote: boolean,
  isSmartBetslipFeatureActive: boolean,
  items: BetSlipItem[]
): SmartBetslipState {
  const fobArr = items.filter(x => x && isFobSelection(x.selection))

  const propSeqs = fobArr
    .filter(x => !x.receipt)
    .flatMap(item =>
      item && isFobMatchedSelection(item.selection) ? [Number(item.selection.propositionSeq)] : []
    )

  let totes: string[] = []

  if (includeTote) {
    totes = items.flatMap(item => {
      if (!item.selection || item.receipt || !isToteSelection(item.selection)) return []

      if (!Number.isInteger(Number(item.selection.selectionString))) return []

      const { fixtureDate, fixtureId, raceNumber, selectionString } = item.selection
      return `${dayjs(fixtureDate).format('YYYY-MM-DD')}#${fixtureId}#${raceNumber}#${selectionString}`
    })
  }

  return {
    shouldAutoAddToBetslip: items.length > 0,
    betslipFobPropSeqs: propSeqs,
    betslipToteIds: totes,
    isSmartBetslipFeatureActive,
  }
}
